/**
 * The command palette's overlay (`state/palette.ts`): a search field over the workspace and the
 * best matches under it. ↑/↓ move, Enter runs, Escape or a click outside closes; a line asking
 * for confirmation is chosen twice.
 */
import { For, Show, createEffect, type JSX } from "solid-js";
import { useApp } from "./context";
import type { PaletteItem, PaletteKind } from "../state/palette";

/** The small word in front of a line saying what it is. */
const KIND_WORD: Record<PaletteKind, string> = { action: "", window: "Window", group: "Group", node: "Node" };

/** The palette, when shown. */
export function CommandPalette(): JSX.Element {
  const { palette } = useApp();
  let list: HTMLDivElement | undefined;
  // Keeps the highlighted line in sight.
  createEffect(() => {
    const index = palette.active();
    if (palette.shown()) list?.querySelectorAll<HTMLElement>("[data-palette-item]")[index]?.scrollIntoView({ block: "nearest" });
  });
  /** Moves, runs or closes from the keyboard. */
  const onKey = (e: KeyboardEvent) => {
    const keys: Record<string, () => void> = {
      ArrowDown: () => palette.move(1),
      ArrowUp: () => palette.move(-1),
      Enter: () => palette.choose(),
      Escape: () => palette.hide(),
    };
    const act = keys[e.key];
    if (!act) return;
    e.preventDefault();
    e.stopPropagation();
    act();
  };
  /** One line of the list: what it is, its label (or what choosing it again does), where it comes from, its shortcut. */
  const line = (item: PaletteItem, i: () => number) => (
    <button
      type="button"
      class="palette-item"
      classList={{ active: palette.active() === i(), danger: !!item.danger }}
      data-palette-item={item.key}
      disabled={item.disabled}
      onMouseMove={() => palette.active() !== i() && palette.setActive(i())}
      onClick={() => palette.choose(item)}
    >
      <span class="palette-check">{item.checked === undefined ? "" : item.checked ? "✓" : ""}</span>
      <span class="palette-label">
        <Show when={KIND_WORD[item.kind]}>
          <span class="palette-kind">{KIND_WORD[item.kind]}</span>
        </Show>
        {palette.armed() === item.key ? item.confirm : item.label}
      </span>
      <span class="palette-where">{item.where}</span>
      <Show when={item.shortcut}>
        <kbd class="palette-key">{item.shortcut}</kbd>
      </Show>
    </button>
  );
  return (
    <Show when={palette.shown()}>
      <div class="palette-backdrop" onPointerDown={(e) => e.target === e.currentTarget && palette.hide()}>
        <div class="palette" id="palette" role="dialog" aria-label="Find an action">
          <input
            id="palette-input"
            class="palette-input"
            type="search"
            placeholder="Find an action, a window, a node…"
            autocomplete="off"
            spellcheck={false}
            value={palette.query()}
            ref={(input) => queueMicrotask(() => input.focus())}
            onInput={(e) => palette.setQuery(e.currentTarget.value)}
            onKeyDown={onKey}
          />
          <div class="palette-list" ref={list} role="listbox">
            <For each={palette.results()} fallback={<p class="palette-none muted">Nothing matches “{palette.query()}”.</p>}>
              {(item, i) => line(item, i)}
            </For>
          </div>
        </div>
      </div>
    </Show>
  );
}
